import './ProductCard.scss';
import { ReactNode } from 'react';
import cn from 'classnames';
import { Modal } from 'react-bootstrap';
import { FaGithub, FaSlack } from 'react-icons/fa';
import {
  AUDIENCE_ICON,
  IconChip,
  INTELLIGENCE_ICON,
  KIND_ICON,
  ParentBreadcrumb,
  SCOPE_ICON,
  TagList
} from './ProductChips';
import { quadrantOf, QUADRANTS } from './effort-impact-utils';
import { DocsStatus, getStatusBucket, Product } from './product-roadmap-constants';
import { ReferenceDocPanel } from './ReferenceDocPanel';

// Bliss Point App paths are stored relative (company-prefixed with a literal `<company>`
// placeholder) -- the card prepends the real domain so it reads as a full, recognizable URL,
// without repeating the domain in every PRODUCT_FACETS-adjacent data entry.
const BLISS_POINT_DOMAIN = 'blisspoint.tinuiti.com';

interface ProductCardSectionProps {
  title: string;
  children: ReactNode;
  // Description/Links/Documentation hold prose or list-like content that benefits from a
  // container; Details (just Target/Owner) is too light to earn one -- a label is still enough
  // orientation for two short facts.
  boxed?: boolean;
}

// Every section below the header follows the same shape -- a small uppercase label, optionally
// a bordered box -- so the card reads as one consistent, scannable system rather than a pile of
// differently-styled fragments.
const ProductCardSection = ({ title, children, boxed = true }: ProductCardSectionProps) => (
  <div className='product-card-section'>
    <span className='product-card-section-label'>{title}</span>
    <div className={cn({ 'product-card-section-body': boxed })}>{children}</div>
  </div>
);

interface ProductCardProps {
  product: Product | null;
  functionColorMap: Record<string, string>;
  onHide: () => void;
}

// The detail surface beyond what the row can hold: full (untruncated) description, and content
// the table never had a column for at all -- Bliss Point App location, repo, Slack channel,
// and documentation. Recapitulates the row's own chips/capsules rather than inventing a second
// visual language for the same facets (see ProductChips.tsx, shared with ProductRoadmapTable).
export const ProductCard = ({ product, functionColorMap, onHide }: ProductCardProps) => {
  if (!product) return null;

  const bucket = getStatusBucket(product.life);
  const slackUrl = product.slackChannel
    ? `https://tinuiti.slack.com/archives/${product.slackChannel.id}`
    : null;
  const hasLinks = product.appPath.length > 0 || Boolean(product.repoUrl) || Boolean(slackUrl);
  const { assessment } = product;
  const quadrant = assessment && QUADRANTS.find(q => q.key === quadrantOf(assessment));

  return (
    <Modal
      className='product-card-modal'
      show
      onHide={onHide}
      dialogClassName='product-card-dialog'
    >
      <Modal.Header closeButton>
        <div className='product-card-header'>
          <div className='product-card-title-row'>
            <span className='product-card-title-identity'>
              <span className='product-card-title'>{product.name}</span>
              <span
                className={cn('product-card-status', {
                  [`design-${bucket.style.design}`]: bucket.style.design,
                  [`intent-${bucket.style.intent}`]: bucket.style.intent,
                  [`custom-${bucket.style.custom}`]: bucket.style.custom
                })}
              >
                {bucket.label}
              </span>
            </span>
            <span className='product-card-title-icons'>
              <IconChip facet='kind' value={product.kind} iconMap={KIND_ICON} />
              <IconChip facet='audience' value={product.audience} iconMap={AUDIENCE_ICON} />
              <IconChip facet='scope' value={product.scope} iconMap={SCOPE_ICON} />
              {(product.intelligence ?? []).map(value => (
                <IconChip
                  key={value}
                  facet='intelligence'
                  value={value}
                  iconMap={INTELLIGENCE_ICON}
                />
              ))}
            </span>
          </div>
          <ParentBreadcrumb parent={product.parent} />
        </div>
      </Modal.Header>
      <Modal.Body className='product-card-body'>
        <TagList values={product.function} colorMap={functionColorMap} />

        <ProductCardSection title='Description'>
          <p className='product-card-description'>
            {product.desc || product.oneLiner || <em>No description on file</em>}
          </p>
        </ProductCardSection>

        {assessment && (
          <ProductCardSection title='Effort × Impact' boxed={false}>
            <div className='product-card-facts'>
              <div className='product-card-fact'>
                <span className='product-card-fact-label'>Impact</span>
                <span className='product-card-fact-value'>{assessment.impact} / 10</span>
              </div>
              <div className='product-card-fact'>
                <span className='product-card-fact-label'>Effort remaining</span>
                <span className='product-card-fact-value'>{assessment.effort} / 10</span>
              </div>
              <div className='product-card-fact'>
                <span className='product-card-fact-label'>Quadrant</span>
                <span className='product-card-fact-value'>
                  {quadrant?.label}
                  {assessment.draft ? ' (draft, under team review)' : ''}
                </span>
              </div>
            </div>
          </ProductCardSection>
        )}

        <ProductCardSection title='Details' boxed={false}>
          <div className='product-card-facts'>
            <div className='product-card-fact'>
              <span className='product-card-fact-label'>Target</span>
              <span className='product-card-fact-value'>{product.target}</span>
            </div>
            <div className='product-card-fact'>
              <span className='product-card-fact-label'>Owner</span>
              <span className='product-card-fact-value'>{product.owner || '—'}</span>
            </div>
          </div>
        </ProductCardSection>

        {hasLinks && (
          <ProductCardSection title='Links'>
            <div className='product-card-links'>
              {product.appPath.map(path => (
                <div className='product-card-link-row' key={path}>
                  <img
                    className='product-card-link-icon'
                    src='/tinuiti_favicon.ico'
                    alt='Bliss Point App'
                  />
                  <span className='product-card-link-value product-card-link-value-plain'>
                    {BLISS_POINT_DOMAIN}/{path}
                  </span>
                </div>
              ))}
              {product.slackChannel && slackUrl && (
                <div className='product-card-link-row'>
                  <a
                    className='product-card-link-value'
                    href={slackUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <FaSlack className='product-card-link-icon' />#{product.slackChannel.name}
                  </a>
                </div>
              )}
              {product.repoUrl && (
                <div className='product-card-link-row'>
                  <a
                    className='product-card-link-value'
                    href={product.repoUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <FaGithub className='product-card-link-icon' />
                    {product.repoUrl.replace('https://github.com/', '')}
                  </a>
                </div>
              )}
            </div>
          </ProductCardSection>
        )}

        <ProductCardSection title='Documentation'>
          <ProductCardDocs product={product} />
        </ProductCardSection>
      </Modal.Body>
    </Modal>
  );
};

interface ProductCardDocsProps {
  product: Product;
}

const ProductCardDocs = ({ product }: ProductCardDocsProps) => {
  if (product.docsStatus === DocsStatus.HAS_REFERENCE) {
    return <ReferenceDocPanel docs={product.docs} />;
  }
  if (product.docsStatus === DocsStatus.SOURCE_IDENTIFIED) {
    return (
      <p className='product-card-docs-note'>
        A documentation source has been identified for this product but not yet distilled into a
        reference note.
      </p>
    );
  }
  return (
    <p className='product-card-docs-note'>
      <em>No documentation identified yet.</em>
    </p>
  );
};
